import React, { useEffect, useState, useCallback } from "react";
import { api, apiError } from "../../lib/api";
import { useI18n } from "../../i18n";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import {
  Library, Plus, Send, CheckCircle2, XCircle, Globe2, FileCheck2,
  Archive, GitBranch, ShieldAlert, Clock, BadgeCheck, Tag,
} from "lucide-react";

const NAVY = "hsl(217 63% 15%)";
const MAKER = ["platform_admin", "tax_admin", "tax_manager", "supervisor", "vat_specialist"];
const REVIEW = ["platform_admin", "tax_admin", "tax_manager", "supervisor", "quality_reviewer", "management"];
const PUBLISH = ["platform_admin", "tax_admin", "tax_manager", "management"];

const kapi = (env) => {
  const h = { "X-Environment": env };
  return {
    get: (u, cfg = {}) => api.get(u, { ...cfg, headers: { ...(cfg.headers || {}), ...h } }),
    post: (u, b, cfg = {}) => api.post(u, b, { ...cfg, headers: { ...(cfg.headers || {}), ...h } }),
    patch: (u, b, cfg = {}) => api.patch(u, b, { ...cfg, headers: { ...(cfg.headers || {}), ...h } }),
  };
};

const STATUS_META = {
  draft: { ar: "مسودّة", en: "Draft", cls: "bg-secondary text-muted-foreground" },
  in_review: { ar: "قيد المراجعة", en: "In review", cls: "bg-[hsl(41_72%_50%)]/18 text-[hsl(41_72%_32%)]" },
  approved: { ar: "معتمد", en: "Approved", cls: "bg-[hsl(217_63%_25%)]/12 text-[hsl(217_63%_25%)]" },
  published: { ar: "منشور", en: "Published", cls: "bg-[hsl(152_55%_45%)]/15 text-[hsl(152_55%_30%)]" },
  superseded: { ar: "مُستبدَل", en: "Superseded", cls: "bg-secondary text-muted-foreground line-through" },
  retired: { ar: "مسحوب", en: "Retired", cls: "bg-[hsl(354_60%_45%)]/12 text-[hsl(354_60%_40%)]" },
};

function Card({ children, className = "", ...r }) {
  return <div className={`bg-white rounded-xl border border-border p-4 ${className}`} {...r}>{children}</div>;
}
function Badge({ status }) {
  const m = STATUS_META[status] || STATUS_META.draft;
  const { lang } = useI18n();
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${m.cls}`} data-testid={`ks-status-${status}`}>{lang === "ar" ? m.ar : m.en}</span>;
}

export default function KnowledgeSources() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const L = (ar, en) => (lang === "ar" ? ar : en);
  const role = user?.role;
  const [env, setEnv] = useState("demo");
  const [summary, setSummary] = useState(null);
  const [sources, setSources] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const s = kapi(env);

  const load = useCallback(() => {
    const k = kapi(env);
    k.get(`/knowledge/sources${filter ? `?status=${filter}` : ""}`).then(({ data }) => setSources(data.sources)).catch((e) => toast.error(apiError(e)));
    k.get("/knowledge/catalogue").then(({ data }) => setSummary(data)).catch(() => {});
    setSelected(null);
  }, [filter, env]);
  useEffect(() => { load(); }, [load]);

  const act = (id, path, body) => s.post(`/knowledge/sources/${id}/${path}`, body || {})
    .then(() => { toast.success(L("تم", "Done")); load(); })
    .catch((e) => toast.error(apiError(e)));

  const openDetail = (id) => s.get(`/knowledge/sources/${id}`).then(({ data }) => setSelected(data)).catch((e) => toast.error(apiError(e)));
  const quarantine = () => s.post("/knowledge/maintenance/quarantine-synthetic")
    .then(({ data }) => { toast.success(L("تم حجر المصادر الاصطناعية", "Synthetic sources quarantined") + ` (${data.quarantined ?? 0})`); load(); })
    .catch((e) => toast.error(apiError(e)));

  return (
    <div className="space-y-6" data-testid="knowledge-sources">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold flex items-center gap-2" style={{ color: NAVY }}>
            <Library className="h-6 w-6" /> {L("حوكمة مصادر المعرفة", "Knowledge-Source Governance")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{L("دورة حياة محكومة للمصادر النظامية الرسمية — داخلي فقط. لا يصبح المصدر مؤهلاً للإسناد إلا بعد التحقق والنشر.", "Controlled lifecycle for official regulatory sources — internal only. A source is grounding-eligible only after verification and publishing.")}</p>
        </div>
        {MAKER.includes(role) && (
          <button data-testid="ks-create-btn" onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1.5 text-white rounded-full px-4 py-2 text-sm font-semibold" style={{ backgroundColor: NAVY }}>
            <Plus className="h-4 w-4" /> {L("مصدر جديد", "New source")}
          </button>
        )}
      </div>

      <div className="flex items-center gap-2" data-testid="ks-env-switch">
        <span className="text-[11px] text-muted-foreground">{L("البيئة", "Environment")}:</span>
        {["demo", "production"].map((e) => (
          <button key={e} data-testid={`ks-env-${e}`} onClick={() => setEnv(e)}
            className={`px-3 py-1 rounded-full text-xs font-semibold border ${env === e ? "text-white border-transparent" : "bg-white text-foreground/70 border-border"}`}
            style={env === e ? { backgroundColor: NAVY } : {}}>
            {e === "demo" ? L("تجريبي", "Demo") : L("الإنتاج", "Production")}
          </button>
        ))}
        {env === "demo" && summary?.shared_from_production > 0 && (
          <span className="text-[10px] text-muted-foreground" data-testid="ks-shared-note">
            {L(`+${summary.shared_from_production} مشتركة من الإنتاج (قراءة فقط)`, `+${summary.shared_from_production} shared from production (read-only)`)}
          </span>
        )}
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="ks-summary">
          <Stat tid="total" label={L("الإجمالي", "Total")} value={summary.total} />
          {Object.entries(summary.by_status).map(([k, v]) => (
            <Stat key={k} tid={k} label={L(STATUS_META[k]?.ar, STATUS_META[k]?.en)} value={v} />
          ))}
        </div>
      )}
      <Card className="flex flex-wrap items-center gap-4 text-[11px]" data-testid="ks-grounding-note">
        <span className="flex items-center gap-1.5"><FileCheck2 className="h-4 w-4 text-[hsl(152_55%_40%)]" />{L("مؤهّلة للإسناد", "Grounding-eligible")}: <b>{summary?.grounding_eligible ?? 0}</b></span>
        <span className="flex items-center gap-1.5" data-testid="ks-stat-verified"><BadgeCheck className="h-4 w-4 text-[hsl(152_55%_40%)]" />{L("محقّقة مهنياً", "Professionally verified")}: <b>{summary?.professionally_verified ?? 0}</b></span>
        <span className="flex items-center gap-1.5" data-testid="ks-stat-ready"><FileCheck2 className="h-4 w-4 text-[hsl(217_63%_35%)]" />{L("جاهزة لإسناد الذكاء الاصطناعي", "Grounding-ready (AI)")}: <b>{summary?.grounding_ready ?? 0}</b></span>
        <span className="flex items-center gap-1.5" data-testid="ks-stat-quarantined"><Archive className="h-4 w-4 text-[hsl(354_60%_45%)]" />{L("محجوزة (اصطناعية)", "Quarantined (synthetic)")}: <b>{summary?.quarantined ?? 0}</b></span>
        {PUBLISH.includes(role) && (
          <button data-testid="ks-quarantine-btn" onClick={quarantine}
            className="ms-auto inline-flex items-center gap-1 text-[10px] rounded-full border border-[hsl(354_60%_45%)]/40 text-[hsl(354_60%_40%)] px-2.5 py-1 hover:bg-[hsl(354_60%_45%)]/8">
            <ShieldAlert className="h-3 w-3" /> {L("حجر المصادر الاصطناعية", "Quarantine synthetic")}
          </button>
        )}
      </Card>

      <div className="flex gap-1 flex-wrap" data-testid="ks-filters">
        {["", "draft", "in_review", "approved", "published", "superseded", "retired"].map((s) => (
          <button key={s || "all"} data-testid={`ks-filter-${s || "all"}`} onClick={() => setFilter(s)}
            className={`px-3 py-1 rounded-full text-xs font-semibold border ${filter === s ? "text-white border-transparent" : "bg-white text-foreground/70 border-border"}`}
            style={filter === s ? { backgroundColor: NAVY } : {}}>
            {s ? L(STATUS_META[s].ar, STATUS_META[s].en) : L("الكل", "All")}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="space-y-2" data-testid="ks-list">
          {sources.map((s) => (
            <Card key={s.id} className="cursor-pointer hover:border-foreground/30 transition-colors" data-testid={`ks-row-${s.id}`} onClick={() => openDetail(s.id)}>
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{lang === "ar" && s.title_ar ? s.title_ar : s.title_en}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="bg-secondary px-1.5 py-0.5 rounded">{s.category}</span>
                    <span>v{s.version}</span>
                    {s.article_code && <span className="font-mono">{s.article_code}</span>}
                    {s.scope === "global" && <Globe2 className="h-3 w-3" />}
                    {s.read_only && <span className="italic text-[hsl(217_63%_35%)]" data-testid="ks-shared-badge">{L("مشترك (قراءة فقط)", "shared (read-only)")}</span>}
                    {s.migrated_from_catalogue && <span className="italic">{L("مُرحّل (بيانات وصفية)", "migrated (metadata)")}</span>}
                  </div>
                </div>
                <Badge status={s.status} />
              </div>
            </Card>
          ))}
          {!sources.length && <Card><div className="text-[11px] text-muted-foreground">{L("لا مصادر.", "No sources.")}</div></Card>}
        </div>

        <div>
          {selected ? <Detail s={selected} L={L} role={role} act={act} env={env} reload={() => openDetail(selected.id)} />
            : <Card data-testid="ks-detail-empty"><div className="text-[11px] text-muted-foreground">{L("اختر مصدراً لعرض التفاصيل ودورة الحياة.", "Select a source to view details and lifecycle.")}</div></Card>}
        </div>
      </div>

      {showCreate && <CreateModal L={L} env={env} onClose={() => setShowCreate(false)} onDone={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}

function Stat({ label, value, tid }) {
  return (
    <div className="rounded-lg border border-border px-3 py-2.5 bg-white" data-testid={`ks-stat-${tid}`}>
      <div className="text-[10px] text-muted-foreground uppercase tracking-wide truncate">{label}</div>
      <div className="text-lg font-bold mt-0.5">{value}</div>
    </div>
  );
}

function Detail({ s, L, role, act, env, reload }) {
  const { lang } = useI18n();
  const [editing, setEditing] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [indexing, setIndexing] = useState(false);
  const ro = !!s.read_only;
  const AC = { binding: L("مُلزِم", "Binding"), guidance: L("إرشاد", "Guidance"), persuasive: L("استئناسي", "Persuasive") };
  const groundingReady = s.status === "published" && s.grounding_eligible && s.professionally_verified
    && s.official_reference_url && s.effective_from && !s.quarantined;
  const canMake = MAKER.includes(role) && !ro;
  const canReview = REVIEW.includes(role) && !ro;
  const canPublish = PUBLISH.includes(role) && !ro;
  const doIndex = () => {
    setIndexing(true);
    kapi(env).post(`/knowledge/index/${s.id}`)
      .then(({ data }) => toast.success(L(`تمت الفهرسة — ${data.chunks} مقطع (${data.reused ? "من الذاكرة" : "جديد"})`, `Indexed — ${data.chunks} chunks (${data.reused ? "cached" : "new"})`)))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => { setIndexing(false); reload(); });
  };
  return (
    <Card data-testid="ks-detail">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-bold">{lang === "ar" && s.title_ar ? s.title_ar : s.title_en}</div>
          <div className="text-[11px] text-muted-foreground">{s.authority} · {s.category} · v{s.version}</div>
        </div>
        <Badge status={s.status} />
      </div>
      {ro && (
        <div className="mt-2 flex items-center gap-1.5 text-[10px] text-[hsl(217_63%_35%)] bg-[hsl(217_63%_25%)]/8 rounded px-2 py-1" data-testid="ks-readonly-banner">
          <ShieldAlert className="h-3.5 w-3.5" /> {L("مصدر إنتاج مشترك للقراءة فقط — لا يمكن تعديله من البيئة التجريبية.", "Read-only shared production source — cannot be modified from Demo.")}
        </div>
      )}

      <div className="mt-3 space-y-1 text-[11px]" data-testid="ks-detail-fields">
        <Field label={L("الرمز المرجعي", "Article code")} value={s.article_code || "—"} />
        <Field label={L("المصدر الرسمي", "Official reference")} value={s.official_reference_url || "—"} />
        <Field label={L("سريان من/إلى", "Effective from/to")} value={`${s.effective_from || "—"} → ${s.effective_to || "—"}`} />
        <Field label={L("آخر تحقّق", "Last verified")} value={s.last_verified_at || L("غير محقّق", "not verified")} />
        <Field label={L("بصمة المحتوى", "Content hash")} value={s.content_hash ? `${s.content_hash.slice(0, 20)}…` : L("لا محتوى", "no content")} />
        <Field label={L("مؤهّل للإسناد", "Grounding eligible")} value={s.grounding_eligible ? L("نعم", "Yes") : L("لا", "No")} />
        <Field label={L("التصنيف القانوني", "Authority class")} value={s.authority_class ? AC[s.authority_class] : "—"} />
        <Field label={L("السيناريوهات", "Scenarios")} value={(s.scenario_tags || []).join(", ") || "—"} />
        <Field label={L("محقّق مهنياً", "Professionally verified")} value={s.professionally_verified ? L(`نعم — ${s.verified_by || ""}`, `Yes — ${s.verified_by || ""}`) : L("لا", "No")} />
        <Field label={L("جاهز لإسناد AI", "Grounding-ready (AI)")} value={groundingReady ? L("نعم", "Yes") : L("لا", "No")} />
        {s.quarantined && <Field label={L("الحالة", "Flag")} value={L("محجوز — مستبعد من الإسناد", "Quarantined — excluded from grounding")} />}
      </div>

      {!s.content && (
        <div className="mt-2 flex items-center gap-1.5 text-[10px] text-[hsl(41_72%_32%)] bg-[hsl(41_72%_50%)]/12 rounded px-2 py-1" data-testid="ks-nocontent-warn">
          <ShieldAlert className="h-3.5 w-3.5" /> {L("لا يمكن النشر بدون محتوى محقّق (العنوان/الرابط وحده لا يكفي كدليل إسناد).", "Cannot publish without verified content (a title/URL alone is not grounding evidence).")}
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {s.status === "draft" && canMake && <Action tid="edit" icon={FileCheck2} label={L("تحرير المحتوى", "Edit content")} onClick={() => setEditing((v) => !v)} />}
        {s.status === "draft" && canMake && <Action tid="submit" icon={Send} label={L("إرسال للمراجعة", "Submit")} onClick={() => act(s.id, "submit")} />}
        {s.status === "in_review" && canReview && <Action tid="approve" icon={CheckCircle2} label={L("اعتماد", "Approve")} onClick={() => act(s.id, "review", { decision: "approve" })} />}
        {s.status === "in_review" && canReview && <Action tid="reject" icon={XCircle} label={L("رفض", "Reject")} onClick={() => act(s.id, "review", { decision: "reject", reason: "revise" })} />}
        {s.status === "approved" && canPublish && <Action tid="publish" icon={Globe2} label={L("نشر", "Publish")} onClick={() => act(s.id, "publish")} />}
        {(s.status === "published" || s.status === "approved") && canPublish && <Action tid="retire" icon={Archive} label={L("سحب", "Retire")} onClick={() => act(s.id, "retire", { reason: "retired" })} />}
        {s.status === "published" && canReview && !s.professionally_verified && <Action tid="verify" icon={BadgeCheck} label={L("تحقّق مهني", "Professional verify")} onClick={() => setVerifying((v) => !v)} />}
        {s.status === "published" && canReview && s.professionally_verified && <Action tid="unverify" icon={XCircle} label={L("إلغاء التحقّق", "Unverify")} onClick={() => act(s.id, "unverify", { reason: "re-review" })} />}
        {groundingReady && canMake && <Action tid="index" icon={Library} label={indexing ? L("جارٍ الفهرسة…", "Indexing…") : L("فهرسة للإسناد (AI)", "Index for AI")} onClick={doIndex} />}
        {canMake && <Action tid="newver" icon={GitBranch} label={L("نسخة جديدة", "New version")} onClick={() => act(s.id, "new-version")} />}
      </div>

      {editing && <EditContent s={s} L={L} env={env} onDone={() => { setEditing(false); reload(); }} />}
      {verifying && <VerifyPanel s={s} L={L} env={env} onDone={() => { setVerifying(false); reload(); }} />}

      <div className="mt-4">
        <div className="text-[10px] font-semibold uppercase text-muted-foreground mb-1">{L("سجل دورة الحياة", "Lifecycle trail")}</div>
        <div className="space-y-1" data-testid="ks-transitions">
          {(s.transitions || []).map((t, i) => (
            <div key={i} className="flex items-center gap-2 text-[10px] text-muted-foreground">
              <Clock className="h-3 w-3" /> <b className="text-foreground">{t.action}</b> · {t.by || "system"} · {(t.at || "").slice(0, 19)} {t.note ? `· ${t.note}` : ""}
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

function Field({ label, value }) {
  return <div className="flex gap-2"><span className="text-muted-foreground w-28 shrink-0">{label}</span><span className="break-all">{value}</span></div>;
}
function Action({ label, icon: Icon, onClick, tid }) {
  return (
    <button data-testid={`ks-action-${tid}`} onClick={onClick}
      className="inline-flex items-center gap-1 text-[11px] rounded border border-border px-2 py-1 hover:bg-secondary">
      <Icon className="h-3 w-3" /> {label}
    </button>
  );
}

function EditContent({ s, L, env, onDone }) {
  const [content, setContent] = useState(s.content || "");
  const [verified, setVerified] = useState(s.last_verified_at || "");
  const [url, setUrl] = useState(s.official_reference_url || "");
  const save = () => kapi(env).patch(`/knowledge/sources/${s.id}`, {
    content, last_verified_at: verified || null, official_reference_url: url,
  }).then(() => { toast.success(L("حُفظ", "Saved")); onDone(); }).catch((e) => toast.error(apiError(e)));
  return (
    <div className="mt-3 space-y-2 border-t border-border pt-3" data-testid="ks-edit">
      <textarea data-testid="ks-edit-content" value={content} onChange={(e) => setContent(e.target.value)} rows={4}
        placeholder={L("المحتوى النظامي المحقّق…", "Verified regulatory content…")} className="w-full text-xs rounded border border-border p-2" />
      <div className="flex gap-2">
        <input data-testid="ks-edit-verified" type="date" value={verified} onChange={(e) => setVerified(e.target.value)} className="text-xs rounded border border-border p-1.5" />
        <input data-testid="ks-edit-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder={L("رابط المصدر الرسمي", "Official source URL")} className="flex-1 text-xs rounded border border-border p-1.5" />
      </div>
      <button data-testid="ks-edit-save" onClick={save} className="text-[11px] rounded text-white px-3 py-1.5" style={{ backgroundColor: NAVY }}>{L("حفظ المحتوى", "Save content")}</button>
    </div>
  );
}

function VerifyPanel({ s, L, env, onDone }) {
  const [ac, setAc] = useState(s.authority_class || "binding");
  const [tags, setTags] = useState(s.scenario_tags || []);
  const [note, setNote] = useState("");
  const toggle = (t) => setTags((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t]));
  const save = () => kapi(env).post(`/knowledge/sources/${s.id}/verify`, { authority_class: ac, scenario_tags: tags, note })
    .then(() => { toast.success(L("تم التحقّق المهني والتوقيع", "Professionally verified & signed off")); onDone(); })
    .catch((e) => toast.error(apiError(e)));
  return (
    <div className="mt-3 space-y-2 border-t border-border pt-3" data-testid="ks-verify">
      <div className="flex items-center gap-1.5 text-[10px] text-[hsl(41_72%_32%)] bg-[hsl(41_72%_50%)]/12 rounded px-2 py-1">
        <ShieldAlert className="h-3.5 w-3.5" /> {L("لا تُحقّق مصدراً دون مراجعة فعلية للنص الرسمي.", "Do not verify without actually reviewing the official text.")}
      </div>
      <div className="flex items-center gap-2 text-[11px]">
        <span className="text-muted-foreground">{L("التصنيف القانوني", "Authority class")}</span>
        <select data-testid="ks-verify-class" value={ac} onChange={(e) => setAc(e.target.value)} className="text-xs rounded border border-border p-1.5">
          <option value="binding">{L("مُلزِم", "Binding")}</option>
          <option value="guidance">{L("إرشاد", "Guidance")}</option>
          <option value="persuasive">{L("استئناسي", "Persuasive")}</option>
        </select>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
        <Tag className="h-3 w-3 text-muted-foreground" />
        {["S1", "S2", "S3", "S4"].map((t) => (
          <button key={t} data-testid={`ks-verify-tag-${t}`} onClick={() => toggle(t)}
            className={`px-2 py-0.5 rounded-full border ${tags.includes(t) ? "text-white border-transparent" : "bg-white border-border"}`}
            style={tags.includes(t) ? { backgroundColor: NAVY } : {}}>{t}</button>
        ))}
      </div>
      <input data-testid="ks-verify-note" value={note} onChange={(e) => setNote(e.target.value)}
        placeholder={L("ملاحظة التحقّق (المرجع المؤكَّد)", "Verification note (confirmed reference)")} className="w-full text-xs rounded border border-border p-1.5" />
      <button data-testid="ks-verify-save" onClick={save} className="inline-flex items-center gap-1 text-[11px] rounded text-white px-3 py-1.5" style={{ backgroundColor: NAVY }}>
        <BadgeCheck className="h-3.5 w-3.5" /> {L("تأكيد التحقّق المهني", "Confirm professional sign-off")}
      </button>
    </div>
  );
}

function CreateModal({ L, env, onClose, onDone }) {
  const [f, setF] = useState({ title_en: "", title_ar: "", category: "VAT", authority: "ZATCA", article_code: "", official_reference_url: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = () => kapi(env).post("/knowledge/sources", f).then(() => { toast.success(L("أُنشئت المسودّة", "Draft created")); onDone(); }).catch((e) => toast.error(apiError(e)));
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="ks-create-modal" onClick={onClose}>
      <div className="bg-white rounded-xl p-5 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-bold">{L("مصدر معرفة جديد", "New knowledge source")}</h3>
        <input data-testid="ks-new-title-en" value={f.title_en} onChange={(e) => set("title_en", e.target.value)} placeholder="Title (EN)" className="w-full text-sm rounded border border-border p-2" />
        <input data-testid="ks-new-title-ar" value={f.title_ar} onChange={(e) => set("title_ar", e.target.value)} placeholder="العنوان (ع)" className="w-full text-sm rounded border border-border p-2" />
        <div className="flex gap-2">
          <select data-testid="ks-new-category" value={f.category} onChange={(e) => set("category", e.target.value)} className="text-sm rounded border border-border p-2">
            <option value="VAT">VAT</option><option value="Excise">Excise</option><option value="RETT">RETT</option>
          </select>
          <input data-testid="ks-new-authority" value={f.authority} onChange={(e) => set("authority", e.target.value)} placeholder="Authority" className="flex-1 text-sm rounded border border-border p-2" />
        </div>
        <input data-testid="ks-new-article" value={f.article_code} onChange={(e) => set("article_code", e.target.value)} placeholder={L("الرمز المرجعي (مثل IR Art. 53)", "Article code (e.g. IR Art. 53)")} className="w-full text-sm rounded border border-border p-2" />
        <input data-testid="ks-new-url" value={f.official_reference_url} onChange={(e) => set("official_reference_url", e.target.value)} placeholder={L("رابط المصدر الرسمي", "Official source URL")} className="w-full text-sm rounded border border-border p-2" />
        <div className="flex justify-end gap-2">
          <button data-testid="ks-new-cancel" onClick={onClose} className="text-sm rounded border border-border px-3 py-1.5">{L("إلغاء", "Cancel")}</button>
          <button data-testid="ks-new-submit" onClick={submit} disabled={!f.title_en} className="text-sm rounded text-white px-3 py-1.5 disabled:opacity-50" style={{ backgroundColor: NAVY }}>{L("إنشاء", "Create")}</button>
        </div>
      </div>
    </div>
  );
}
